import { useEffect, useMemo, useState, useRef, type ReactNode } from "react";

import { useAtom } from "jotai";
import { authenticationAtom } from '../State';
import { useLocation, useNavigate } from "react-router-dom";
import { userAPI } from "../UserAPI";
import { useTranslation } from "react-i18next";
import { ApplicationError } from "../general/Errors";
import { motion, useReducedMotion } from "motion/react";
import { Check, ExternalLink, Loader2, X } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/brand/brand";
import { useCustomerLogo } from "@/hooks/useCustomerLogo";
import { SPRING_LAYOUT, SPRING_SWAP } from "@/lib/ease";
import { cn } from "@/lib/utils";
import PasswordChecklist from "react-password-checklist";
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import AddressEntry from "../components/AddressEntry";
import { useModalContext } from "../components/ModalProvider";
import PhoneInput from "../components/PhoneInput";
import { getStripePromise } from '@/lib/stripeLoader';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { jwtAuthService } from "../JWTAuthService";
import { configuration } from "../Configuration";
import { billingUnitName } from "../real-gain/billingModels";
import {
  AuthError,
  AuthField,
  AuthFootnote,
  AuthHeader,
  AuthShell,
  AuthStep,
  AuthSubmit,
  OrDivider,
  Reveal,
  StepCounter,
} from './auth/AuthLayout';
import { cameFromSibling, markSiblingNavigation } from './auth/authEntrance';

// Stripe configuration
const stripePublishableKey = configuration.stripeKey;

if ((!stripePublishableKey || stripePublishableKey === 'pk_test_your_key_here') && configuration.mode !== 'production') {
  console.warn('⚠️ Stripe publishable key not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable with a valid Stripe test key.');
}

// loaded when the form is shown (lib/stripeLoader.ts), not when this module is imported

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      fontFamily: 'Sansation, sans-serif',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#d32f2f',
    },
  },
  hidePostalCode: true,
};

/**
 * Gemeinsames Gerüst aller Registrierungsschritte: Überschrift mit Unterzeile,
 * darunter die Felder. Auf Modulebene definiert, damit ein erneutes Rendern
 * die Eingabefelder nicht neu einhängt (sonst ginge der Fokus verloren).
 */
const StepCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex flex-col">
    <AuthHeader title={title} subtitle={subtitle} />
    <div className="flex flex-col gap-4">{children}</div>
  </div>
);

/** Back on the left, the primary action filling the rest. */
const StepActions = ({
  onBack,
  backLabel,
  children,
}: {
  onBack: () => void;
  backLabel: string;
  children?: ReactNode;
}) => (
  <div className="mt-2 flex gap-2">
    <Button variant="outline" type="button" className="h-10 shrink-0" onClick={onBack}>
      {backLabel}
    </Button>
    {children && <div className="min-w-0 flex-1">{children}</div>}
  </div>
);

// PaymentStep component that uses Stripe hooks
interface PaymentStepProps {
  onBack: () => void;
  onComplete: (paymentMethodId: string | null) => Promise<void>;
  waiting: boolean;
  billingName: string;
  billingEmail: string;
  t: any; // Translation function
}

function PaymentStep({ onBack, onComplete, waiting, billingName, billingEmail, t }: PaymentStepProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || waiting) return;

    if (!stripe || !elements) {
      setError('Stripe ist nicht richtig konfiguriert.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Kartenelement nicht gefunden');
      }

      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingName,
          email: billingEmail,
        },
      });

      if (stripeError) {
        console.error('Stripe error:', stripeError);
        setError(`Zahlungsmethode konnte nicht erstellt werden: ${stripeError.message}`);
        setLoading(false);
        return;
      }

      console.log('Payment method created:', paymentMethod.id);
      await onComplete(paymentMethod.id);
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Ein Fehler ist aufgetreten.');
    } finally {
      setLoading(false);
    }
  };

  // Check if Stripe is configured
  if (!stripePublishableKey || stripePublishableKey === 'pk_test_your_key_here' || !stripe) {
    return (
      <StepCard title={t('signupPage.paymentMethod')}>
        <Alert variant="warning">
          <AlertTitle>Stripe-Konfiguration erforderlich</AlertTitle>
          <AlertDescription>
            Um Kreditkartenzahlungen zu aktivieren, muss REACT_APP_STRIPE_PUBLISHABLE_KEY konfiguriert werden.
          </AlertDescription>
        </Alert>
        <StepActions onBack={onBack} backLabel={t('global.back')}>
          <Button
            id="noPaymentMethodButton"
            type="button"
            className="h-10 w-full"
            onClick={() => onComplete(null)}
          >
            {t('signupPage.noPaymentMethod', 'No payment method')}
          </Button>
        </StepActions>
      </StepCard>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      <StepCard
        title={t('signupPage.paymentMethod')}
        subtitle={t('signupPage.creditCardPaymentText', 'Bitte geben Sie Ihre Kreditkartendaten ein.')}
      >

        <div className="flex flex-col gap-1.5">
          <p className="text-sm font-medium">{t('signupPage.creditCardData', 'Kreditkartendaten')}</p>
          <div className="min-h-[42px] rounded-md border border-input bg-background px-3 py-2.5">
            <CardElement options={cardElementOptions} />
          </div>
        </div>

        <AuthError message={error} />

        <AuthSubmit id="registerAndOrderButton" className="mt-2" busy={loading || waiting} disabled={!stripe}>
          {t('signupPage.registerAndOrder')}
        </AuthSubmit>
        <StepActions onBack={onBack} backLabel={t('global.back')}>
          <Button
            id="noPaymentMethodButton"
            variant="outline"
            type="button"
            className="h-10 w-full"
            onClick={() => onComplete(null)}
            disabled={loading || waiting}
          >
            {t('signupPage.noPaymentMethod', 'No payment method')}
          </Button>
        </StepActions>
      </StepCard>
    </form>
  );
}

enum PageMode {
  emailEntry = 'emailEntry',
  otpEntry = 'otpEntry',
  passwordEntry = 'passwordEntry',
  companyDataEntry = 'companyDataEntry',
  licenseSelection = 'licenseSelection',
  paymentMethod = 'paymentMethod',
  welcome = 'welcome'
};

const STEP_ORDER: PageMode[] = [
  PageMode.emailEntry,
  PageMode.otpEntry,
  PageMode.passwordEntry,
  PageMode.companyDataEntry,
  PageMode.licenseSelection,
  PageMode.paymentMethod,
  PageMode.welcome,
];

/** Column width per step: the form steps stay narrow, company and licenses widen. */
const STEP_WIDTH: Record<PageMode, number> = {
  [PageMode.emailEntry]: 400,
  [PageMode.otpEntry]: 400,
  [PageMode.passwordEntry]: 440,
  [PageMode.companyDataEntry]: 640,
  [PageMode.licenseSelection]: 760,
  [PageMode.paymentMethod]: 440,
  [PageMode.welcome]: 400,
};


export const SignUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pushError } = useModalContext() as any;
  const location = useLocation();
  const [enterFromSibling] = useState(cameFromSibling);
  const { search } = location;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [userID, setUserID] = useState();
  const [email, setEmail] = useState(searchParams.get("email") || '');
  const [emailBlurred, setEmailBlurred] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [validationCode, setValidationCode] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(true);
  const [mobileNumber, setMobileNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState({}) as any;
  const [tradeRegisterId, setTradeRegisterId] = useState('');
  const [tradeRegisterCity, setTradeRegisterCity] = useState('');
  const [taxId, setTaxId] = useState('');
  const [, setAuthentication] = useAtom(authenticationAtom);
  const [pageMode, setPageMode] = useState(PageMode.emailEntry);
  // Forward steps slide in from the right, "back" from the left.
  const [direction, setDirection] = useState<1 | -1>(1);
  const reduce = useReducedMotion();
  const goTo = (next: PageMode) => {
    setDirection(STEP_ORDER.indexOf(next) < STEP_ORDER.indexOf(pageMode) ? -1 : 1);
    setPageMode(next);
  };
  const [platformServicesMarketplaceOffer, setPlatformServicesMarketplaceOffer] = useState() as any;
  const [error, setError] = useState() as any;
  const [selectedPlan, setSelectedPlan] = useState() as any;
  const [voucherCode, setVoucherCode] = useState('');
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [voucherError, setVoucherError] = useState('');
  const [customPlanFromVoucher, setCustomPlanFromVoucher] = useState<any>(null);
  const [waiting, setWaiting] = useState(false);
  const validationCodeFieldRef = useRef<HTMLInputElement>(null);
  const emailFieldRef = useRef<HTMLInputElement>(null);
  const passwordFieldRef = useRef<HTMLInputElement>(null);
  const companyNameFieldRef = useRef<HTMLInputElement>(null);
  const { logo, isFallback: isFallbackLogo } = useCustomerLogo();

  useEffect(() => {
    const call = async () => {
      try {
        const marketplaceOffer = await userAPI.getPlatformServicesMarketplaceOffer();
        marketplaceOffer.plans = marketplaceOffer.plans.map((plan: any) => {
          return {
            ...plan,
            _fromVoucher: false,
            _voucherCode: null,
            serviceReference: String(marketplaceOffer._id),
            _offerName: plan.name
          };
        });
        setPlatformServicesMarketplaceOffer(marketplaceOffer);
        // Set default selection to first plan if available
        if (marketplaceOffer?.plans && marketplaceOffer.plans.length > 0) {
          setSelectedPlan(marketplaceOffer.plans[0]);
        }
      } catch (error: any) {
        console.error('Failed to fetch platform services marketplace offer:', error);
        // Don't use pushError here as it redirects to login on UnauthorizedError
        // Just log the error and show a message to the user
        if (error instanceof ApplicationError) {
          setError(t(`errors.${error.message}`));
        } else {
          setError(t('signupPage.failedToLoadPlans') || 'Failed to load plans. Please try again later.');
        }
      }
    };

    call();
  }, [t]);

  useEffect(() => {
    if (pageMode === PageMode.emailEntry && emailFieldRef.current) {
      // Small delay to ensure the field is rendered
      setTimeout(() => {
        emailFieldRef.current?.focus();
      }, reduce ? 0 : 380);
    }
  }, [pageMode, reduce]);

  useEffect(() => {
    if (pageMode === PageMode.otpEntry && validationCodeFieldRef.current) {
      // Small delay to ensure the field is rendered
      setTimeout(() => {
        validationCodeFieldRef.current?.focus();
      }, reduce ? 0 : 380);
    }
  }, [pageMode, reduce]);

  useEffect(() => {
    if (pageMode === PageMode.passwordEntry && passwordFieldRef.current) {
      // Small delay to ensure the field is rendered
      setTimeout(() => {
        passwordFieldRef.current?.focus();
      }, reduce ? 0 : 380);
    }
  }, [pageMode, reduce]);

  useEffect(() => {
    if (pageMode === PageMode.companyDataEntry && companyNameFieldRef.current) {
      // Small delay to ensure the field is rendered
      setTimeout(() => {
        companyNameFieldRef.current?.focus();
      }, reduce ? 0 : 380);
    }
  }, [pageMode, reduce]);

  useEffect(() => {
    // Clear selected plan if it's a voucher plan (has customer field) when on license selection page
    // But don't clear plans that come from vouchers (marked with _fromVoucher)
    if (pageMode === PageMode.licenseSelection && selectedPlan && selectedPlan.customer && !selectedPlan._fromVoucher) {
      setSelectedPlan(null);
    }
  }, [pageMode, selectedPlan, platformServicesMarketplaceOffer]);

  useEffect(() => {
    // Auto-select the voucher plan when it becomes available
    if (customPlanFromVoucher && pageMode === PageMode.licenseSelection) {
      setSelectedPlan(customPlanFromVoucher);
    }
  }, [customPlanFromVoucher, pageMode]);

  const isEmptyString = (value: string): boolean => {
    return !value || value.trim().length === 0;
  };

  const isValidEmail = (email: string): boolean => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      ) !== null;
  };

  const isValidUUIDV4 = (uuid: string): boolean => {
    console.log('Match >>>', uuid, String(uuid)
      .toUpperCase()
      .match(
        /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/
      ));
    return String(uuid)
      .toUpperCase()
      .match(
        /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/
      ) !== null;
  };

  const isMobileNumberValid = (mobileNumber: string): boolean => {
    return isPossiblePhoneNumber(mobileNumber || '');
  }

  const isAddressValid = (): boolean => {
    return !!(companyAddress?.street && companyAddress.street.trim().length > 0 &&
              companyAddress?.streetNumber && companyAddress.streetNumber.trim().length > 0 &&
              companyAddress?.postalCode && companyAddress.postalCode.trim().length > 0 &&
              companyAddress?.city && companyAddress.city.trim().length > 0);
  }

  const formatBillingInfo = (billing: any): string => {
    const algorithmName = billingUnitName(billing.algorithm, billing.name);
    const offset = billing.offset || 0;
    const fee = billing.fee || 0;
    return `${offset} ${algorithmName} enthalten, ${fee.toFixed(2)} € pro weitere ${algorithmName}`;
  }

  // Format-Hinweise des Bestands: der Eingabe-Shim verschluckte
  // `onGetErrorMessage`, die Meldungen sind jetzt wieder sichtbar.
  const emailFormatError = emailBlurred && !isEmptyString(email) && !isValidEmail(email)
    ? t('signupPage.invalidEmailFormat')
    : '';
  const validationCodeFormatError = !isEmptyString(validationCode) && !isValidUUIDV4(validationCode)
    ? t('signupPage.invalidValidationCodeFormat')
    : '';

  const submitPersonalData = async (e: React.FormEvent) => {
    e.preventDefault();
    if (waiting || isEmptyString(email) || isEmptyString(firstName) || isEmptyString(lastName) || !isValidEmail(email)) return;
    setError(null);
    setWaiting(true);
    try {
      // @ts-ignore
      const result = await userAPI.checkEmail(email, firstName, lastName);

      setUserID(result.id)
      goTo(PageMode.otpEntry);
    } catch (error) {
      if (error instanceof ApplicationError) {
        // @ts-ignore
        setError(t(`errors.${error.message}`, { email, domain: email.substring(email.indexOf('@') + 1) }));
      } else {
        pushError(error);
      }
    } finally {
      setWaiting(false);
    }
  };

  const submitValidationCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (waiting || isEmptyString(validationCode) || !isValidUUIDV4(validationCode)) return;
    setError(null);
    setWaiting(true);
    try {
      // @ts-ignore
      await userAPI.validateEmail(email, validationCode);

      goTo(PageMode.passwordEntry);
    } catch (error) {
      if (error instanceof ApplicationError) {
        // @ts-ignore
        setError(t(`errors.${error.message}`,));
      } else {
        pushError(error);
      }
    } finally {
      setWaiting(false);
    }
  };

  const passwordStepComplete = !isEmptyString(password) && !isEmptyString(repeatPassword) && passwordValid && !isEmptyString(mobileNumber) && isMobileNumberValid(mobileNumber);
  const companyStepComplete = !isEmptyString(companyName) && isAddressValid();

  const renderStep = () => {
    switch (pageMode) {
      case PageMode.emailEntry: return (
        <form className="flex flex-col" onSubmit={submitPersonalData} noValidate>
          <StepCard
            title={t('signupPage.signUpTitle')}
            subtitle={
              <>
                {t('signupPage.haveAccount')}{' '}
                <button
                  type="button"
                  onClick={() => { markSiblingNavigation(); navigate('/login'); }}
                  className="rounded-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('signupPage.signIn')}
                </button>
              </>
            }
          >
            <AuthField
              id="emailField"
              ref={emailFieldRef}
              label={t('signupPage.email')}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={t('loginPage.emailPlaceholder')}
              value={email}
              error={emailFormatError}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setEmailBlurred(true)}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <AuthField
                id="firstNameField"
                label={t('signupPage.firstName')}
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
              <AuthField
                id="lastNameField"
                label={t('signupPage.lastName')}
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
            <AuthError message={error} />
            <AuthSubmit
              id="nextButton1"
              className="mt-2"
              busy={waiting}
              disabled={isEmptyString(email) || isEmptyString(firstName) || isEmptyString(lastName) || !isValidEmail(email)}
            >
              {t('global.next')}
            </AuthSubmit>
            <a
              href="/markup/legal/Datenschutzbestimmungen.md"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 self-start text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              {t('signupPage.dataProtectionLink')}
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            </a>
          </StepCard>
        </form>
      );
      case PageMode.otpEntry: return (
        <form className="flex flex-col" onSubmit={submitValidationCode} noValidate>
          <StepCard
            title={t('signupPage.checkYourInbox')}
            subtitle={t('signupPage.weSentYouAValidationCode', { email })}
          >
            <AuthField
              id="validationCodeField"
              ref={validationCodeFieldRef}
              label={t('signupPage.validationCode')}
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              className="font-mono text-[13px] tracking-tight"
              placeholder="xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx"
              value={validationCode}
              error={validationCodeFormatError}
              onChange={(event) => setValidationCode(event.target.value.trim())}
            />
            <AuthError message={error} />
            <AuthSubmit id="nextButton2" className="mt-2" busy={waiting} disabled={isEmptyString(validationCode)}>
              {t('global.next')}
            </AuthSubmit>
            <AuthFootnote
              className="mt-2"
              text={t('signupPage.wrongEmail')}
              action={t('global.back')}
              onAction={() => {
                setError(null);
                setValidationCode('');
                goTo(PageMode.emailEntry);
              }}
            />
          </StepCard>
        </form>
      );
      case PageMode.passwordEntry: return (
        <form className="flex flex-col" noValidate onSubmit={(e) => {
          e.preventDefault();
          if (passwordStepComplete) goTo(PageMode.companyDataEntry);
        }}>
          <StepCard title={t('signupPage.passwordAndMobileNumber')} subtitle={t('signupPage.whyMobileNumber')}>
            <AuthField
              id="passwordField"
              ref={passwordFieldRef}
              label={t('signupPage.password')}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <AuthField
              id="repeatPasswordField"
              label={t('signupPage.passwordRepeat')}
              type="password"
              autoComplete="new-password"
              value={repeatPassword}
              onChange={(event) => setRepeatPassword(event.target.value)}
            />
            <PasswordChecklist
              rules={["minLength", "specialChar", "number", "capital", "match"]}
              minLength={8}
              value={password || ''}
              valueAgain={repeatPassword}
              className="text-sm"
              itemClassName="text-sm"
              validTextColor="hsl(var(--foreground))"
              invalidTextColor="hsl(var(--muted-foreground))"
              iconComponents={{
                ValidIcon: <Check aria-hidden="true" className="mr-1.5 h-[18px] w-[18px] shrink-0 text-success" />,
                InvalidIcon: <X aria-hidden="true" className="mr-1.5 h-[18px] w-[18px] shrink-0 text-destructive" />,
              }}
              messages={{
                minLength: t('common.passwordChangePanel.passwordRuleMinLength'),
                specialChar: t('common.passwordChangePanel.passwordRuleSpecialChar'),
                number: t('common.passwordChangePanel.passwordRuleNumber'),
                capital: t('common.passwordChangePanel.passwordRuleCapital'),
                match: t('common.passwordChangePanel.passwordRuleMatch'),
              }}
              onChange={(valid: boolean) => setPasswordValid(valid)}
            />
            {/* PhoneInput ist noch nicht migriert und trägt feste
                Mindestbreiten — der Scrollrahmen hält den Überlauf im Feld
                statt in der Seite. */}
            <div className="min-w-0 overflow-x-auto">
              <PhoneInput
                id="mobileNumberField"
                label={t('signupPage.mobileNumber')}
                value={mobileNumber}
                onChange={(value: string) => setMobileNumber(value as any)}
                onValidationChange={() => { }}
                required
              />
            </div>
            {/* No way back to the code step: the address is confirmed, and
                confirming the same code again would fail. */}
            <AuthSubmit id="nextButton3" className="mt-2" disabled={!passwordStepComplete}>
              {t('global.next')}
            </AuthSubmit>
          </StepCard>
        </form>
      );
      case PageMode.companyDataEntry: return (
        <form className="flex flex-col" noValidate onSubmit={(e) => {
          e.preventDefault();
          if (companyStepComplete) goTo(PageMode.licenseSelection);
        }}>
          <StepCard title={t('signupPage.companyData')}>
            <AuthField
              id="companyNameField"
              ref={companyNameFieldRef}
              label={t('signupPage.companyName')}
              autoComplete="organization"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
            />
            <div className="grid gap-4 sm:grid-cols-3">
              <AuthField
                id="tradeRegisterIdField"
                label={t('signupPage.tradeRegisterId')}
                value={tradeRegisterId}
                onChange={(event) => setTradeRegisterId(event.target.value)}
              />
              <AuthField
                id="tradeRegisterCityField"
                label={t('signupPage.tradeRegisterCity')}
                value={tradeRegisterCity}
                onChange={(event) => setTradeRegisterCity(event.target.value)}
              />
              <AuthField
                id="taxIdField"
                label={t('signupPage.taxId')}
                value={taxId}
                onChange={(event) => setTaxId(event.target.value)}
              />
            </div>
            <p className="pt-2 text-sm font-medium">{t('signupPage.companyAddress')}</p>
            {/* AddressEntry ist noch nicht migriert und trägt feste
                Feldbreiten — der Scrollrahmen hält den Überlauf im Block
                statt in der Seite. */}
            <div className="min-w-0 overflow-x-auto">
              <AddressEntry address={companyAddress} addressChanged={(address: any) => {
                setCompanyAddress(address);
              }}></AddressEntry>
            </div>
            <StepActions onBack={() => goTo(PageMode.passwordEntry)} backLabel={t('global.back')}>
              <AuthSubmit id="nextButton4" disabled={!companyStepComplete}>
                {t('global.next')}
              </AuthSubmit>
            </StepActions>
          </StepCard>
        </form>
      );
      case PageMode.licenseSelection: return (
        <form className="flex flex-col" noValidate onSubmit={(e) => {
          e.preventDefault();
          if (selectedPlan) goTo(PageMode.paymentMethod);
        }}>
          <StepCard
            title={t('signupPage.licenses')}
            subtitle={platformServicesMarketplaceOffer?.plans ? <>
              {t('signupPage.pleaseSelectBeforeLink', { brandName: BRAND.name })}
              <a
                href="https://www.real-gain.org"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >{t('signupPage.pleaseSelectLink')}</a>
              {t('signupPage.pleaseSelectAfterLink', { brandName: BRAND.name })}
            </> : undefined}
          >
            {platformServicesMarketplaceOffer?.plans ?
              <>
                <div
                  role="radiogroup"
                  aria-label={t('signupPage.licenses')}
                  className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3"
                >
                  {platformServicesMarketplaceOffer.plans
                    .filter((plan: any) => !plan.customer || plan._fromVoucher)
                    .map((plan: any) => {
                      const selected = plan === selectedPlan;
                      return (
                        <button
                          key={plan.id || plan.name}
                          id={`plan-${plan.id || plan.name}`}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => setSelectedPlan(plan)}
                          className={cn(
                            "relative flex min-w-0 flex-col rounded-lg border p-4 text-left transition-colors hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                            selected ? "border-foreground/60" : "border-border",
                          )}
                        >
                          {selected && (
                            <motion.span
                              layoutId="signup-plan-selection"
                              className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-primary"
                              transition={reduce ? { duration: 0 } : SPRING_LAYOUT}
                            />
                          )}
                          <span className="font-semibold">
                            {plan._fromVoucher && <span className="text-primary">★ </span>}
                            {plan.name}
                          </span>
                          <span className="mt-1 min-h-[60px] text-sm text-muted-foreground">
                            {(plan.description?.length > 100)
                              ? plan.description.slice(0, 100) + '…'
                              : plan.description}
                          </span>
                          {plan.baseFee !== undefined && (
                            <span className="mt-1 text-sm">
                              <span className="font-medium">{t('signupPage.monthlyFee')}:</span> {(plan.baseFee || 0).toFixed(2)} €
                            </span>
                          )}
                          {plan.term && (
                            <span className="mt-1 text-sm">
                              <span className="font-medium">{t('signupPage.term')}:</span> {plan.term} {t('signupPage.months')}
                            </span>
                          )}
                          {plan.billings && plan.billings.length > 0 && (
                            <span className="mt-1 text-sm text-muted-foreground">
                              {plan.billings.map((billing: any, index: number) => (
                                <span key={index} className="block">
                                  {formatBillingInfo(billing)}
                                </span>
                              ))}
                            </span>
                          )}
                        </button>
                      );
                    })}
                </div>
                <OrDivider label={t('loginPage.or')} />
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">{t('signupPage.voucherCodeQuestion')}</p>
                  <div className="flex w-full flex-wrap items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <AuthField
                        id="voucherCodeField"
                        label={<span className="sr-only">{t('signupPage.voucherCodePlaceholder')}</span>}
                        placeholder={t('signupPage.voucherCodePlaceholder')}
                        value={voucherCode}
                        onChange={(event) => setVoucherCode(event.target.value || '')}
                      />
                    </div>
                    <Button
                      id="validateVoucherButton"
                      type="button"
                      variant="outline"
                      className="h-10"
                      disabled={isEmptyString(voucherCode) || validatingVoucher}
                      onClick={async () => {
                        if (!voucherCode) return;

                        setValidatingVoucher(true);
                        setVoucherError('');

                        try {
                          // Validate voucher - offer ID is extracted from the JWT
                          const result = await userAPI.validateVoucher(voucherCode);

                          if (result.plan) {
                            // Mark plan as from voucher for identification
                            const planFromVoucher = {
                              ...result.plan,
                              _fromVoucher: true,
                              _voucherCode: voucherCode,
                              serviceReference: result.offerId,
                              _offerName: result.offerName
                            };

                            // Remove any existing voucher plan first
                            const plansWithoutVoucher = (platformServicesMarketplaceOffer?.plans || []).filter(
                              (p: any) => !p._fromVoucher
                            );

                            // Add the voucher plan to the plans array
                            if (platformServicesMarketplaceOffer) {
                              setPlatformServicesMarketplaceOffer({
                                ...platformServicesMarketplaceOffer,
                                plans: [...plansWithoutVoucher, planFromVoucher]
                              });
                            }

                            // Store and auto-select the custom plan
                            setCustomPlanFromVoucher(planFromVoucher);
                            setSelectedPlan(planFromVoucher);
                          } else {
                            setVoucherError(t('signupPage.invalidVoucher', 'Invalid or expired voucher code'));
                          }
                        } catch (error: any) {
                          console.error('Failed to validate voucher:', error);
                          setVoucherError(error.message || t('signupPage.voucherValidationFailed', 'Failed to validate voucher'));
                        } finally {
                          setValidatingVoucher(false);
                        }
                      }}
                    >
                      {validatingVoucher && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                      {validatingVoucher ? t('signupPage.validating', 'Validating...') : t('signupPage.validateVoucher')}
                    </Button>
                  </div>
                  <AuthError message={voucherError} />
                  <Reveal show={!!customPlanFromVoucher}>
                    <p className="text-sm text-success">
                      {t('signupPage.voucherApplied', 'Voucher applied! Your custom plan has been added.')}
                    </p>
                  </Reveal>
                  <p className="text-sm text-muted-foreground">
                    {t('signupPage.voucherCodeContactInfo')}{' '}
                    <a
                      href="mailto:sales@the-real-insight.ai"
                      className="font-medium text-foreground underline-offset-4 hover:underline"
                    >sales@the-real-insight.ai</a>
                  </p>
                </div>
                <a
                  href="/markup/license/Lizenzmodelle.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  {t('signupPage.orderTerms')}
                  <ExternalLink aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                </a>
                <StepActions onBack={() => goTo(PageMode.companyDataEntry)} backLabel={t('global.back')}>
                  <AuthSubmit id="nextButton5" disabled={!selectedPlan}>
                    {t('global.next')}
                  </AuthSubmit>
                </StepActions>
              </>
              :
              <div className="flex flex-col gap-3">
                {/* Der Bestand setzt `error` beim fehlgeschlagenen Laden der
                    Lizenzen, zeigte ihn hier aber nie an — die Seite blieb
                    stumm im Ladezustand. */}
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                {!error && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                    <span>{t('signupPage.loadingPlans') || 'Loading plans...'}</span>
                  </div>
                )}
                <StepActions onBack={() => goTo(PageMode.companyDataEntry)} backLabel={t('global.back')} />
              </div>
            }
          </StepCard>
        </form>
      );
      case PageMode.paymentMethod: return (
        <div className="flex flex-col gap-4">
          {/* Der Bestand setzt `error`, wenn `registerAndOrder` fachlich
              scheitert, zeigte ihn aber nirgends an. */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Elements stripe={getStripePromise(stripePublishableKey)}>
            <PaymentStep
              onBack={() => goTo(PageMode.licenseSelection)}
              onComplete={async (pmId: string | null) => {
                setWaiting(true);
                try {
                  const user = {
                    id: userID,
                    email,
                    firstName,
                    lastName,
                    password,
                    mobileNumber,
                  };
                  const customer = {
                    name: companyName,
                  };
                  const legalPerson = {
                    tradeRegisterId,
                    tradeRegisterCity,
                    taxId,
                    headquarters: companyAddress,
                  };

                  const plans = selectedPlan ? [selectedPlan] : [];
                  const paymentMethod = pmId ? { paymentMethodId: pmId } : null;

                  // @ts-ignore
                  const result = await userAPI.registerAndOrder(user, customer, legalPerson, plans, paymentMethod);
                  setAuthentication(result);
                  if (result.accessToken && result.refreshToken) {
                    jwtAuthService.setTokens({
                      accessToken: result.accessToken,
                      refreshToken: result.refreshToken,
                    });
                  }
                  goTo(PageMode.welcome);
                } catch (error) {
                  if (error instanceof ApplicationError) {
                    // @ts-ignore
                    setError(t(`errors.${error.message}`));
                  } else {
                    pushError(error);
                  }
                } finally {
                  setWaiting(false);
                }
              }}
              waiting={waiting}
              billingName={`${firstName} ${lastName}`}
              billingEmail={email}
              t={t}
            />
          </Elements>
        </div>
      );
      case PageMode.welcome: return (
        <StepCard title={t('signupPage.welcome', { brandName: BRAND.name })} subtitle={t('signupPage.welcomeToTRI')}>
          <motion.div
            className="flex h-11 w-11 items-center justify-center rounded-full bg-success/10 text-success"
            initial={reduce ? false : { scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={SPRING_SWAP}
          >
            <Check aria-hidden="true" className="h-5 w-5" />
          </motion.div>
          <Button id="startAskingButton" type="button" className="h-10 w-full" onClick={() => navigate('/chatbot')}>
            {t('signupPage.startAsking')}
          </Button>
        </StepCard>
      );
    }
  };

  const stepIndex = STEP_ORDER.indexOf(pageMode);
  const showProgress = pageMode !== PageMode.welcome;

  return (
    <AuthShell
      logoSrc={logo}
      logoAlt={isFallbackLogo ? BRAND.name : ''}
      width={STEP_WIDTH[pageMode]}
      // Progress sits in the logo row, so every step's title starts where the
      // sign-in title does.
      headerAction={showProgress ? (
        <StepCounter
          current={stepIndex + 1}
          total={STEP_ORDER.length - 1}
          label={t('signupPage.stepOf', { current: stepIndex + 1, total: STEP_ORDER.length - 1 })}
        />
      ) : undefined}
    >
      <AuthStep
        stepKey={pageMode}
        direction={direction}
        enterOnMount={enterFromSibling}
      >
        {renderStep()}
      </AuthStep>
    </AuthShell>
  );
};
